import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Nav from '../components/nav';
import SEO from '../components/SEO';

const injectStyles = () => {
  if (document.getElementById('pb-post-styles')) return;
  const s = document.createElement('style');
  s.id = 'pb-post-styles';
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Manrope:wght@300;400;500;600&display=swap');
    .pb-post * { box-sizing:border-box; margin:0; padding:0; }
    @keyframes post-blink  { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes post-float  { 0%{transform:translateY(0) rotate(0deg);opacity:0} 20%{opacity:.5} 80%{opacity:.3} 100%{transform:translateY(-180px) rotate(180deg);opacity:0} }
    @keyframes post-fadein { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
    @keyframes post-prog   { from{width:0%} to{width:100%} }
    @keyframes post-scan   { 0%,100%{top:2%} 50%{top:90%} }
    @keyframes post-glow   { 0%,100%{opacity:.4} 50%{opacity:1} }
    @keyframes post-ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
    @keyframes post-ring   { 0%{transform:scale(1);opacity:.7} 100%{transform:scale(2.5);opacity:0} }
    @keyframes post-draw   { from{stroke-dashoffset:400} to{stroke-dashoffset:0} }

    .post-cursor { display:inline-block;width:3px;height:.85em;background:#a855f7;vertical-align:text-bottom;margin-left:2px;border-radius:1px;animation:post-blink .7s step-end infinite; }
    .post-particle { position:absolute;animation:post-float linear infinite;pointer-events:none; }
    .post-slide-enter { animation:post-fadein .6s cubic-bezier(.16,1,.3,1) both; }
    .post-prog-bar { animation:post-prog linear both; }
    .post-ctrl:hover { background:rgba(168,85,247,.12)!important;color:#a855f7!important;border-color:rgba(168,85,247,.4)!important; }
    .post-stage-card { transition:transform .25s,border-color .25s,box-shadow .25s; }
    .post-stage-card:hover { transform:translateY(-6px)!important;box-shadow:0 20px 60px rgba(168,85,247,.15)!important; }
    .post-tool-item:hover { background:rgba(168,85,247,.1)!important;border-color:rgba(168,85,247,.3)!important; }
    .post-dot-active { width:28px!important;border-radius:3px!important;background:#a855f7!important; }
    .post-btn-p:hover { transform:translateY(-3px);box-shadow:0 12px 40px rgba(168,85,247,.35); }
    .post-btn-g:hover { border-color:#a855f7!important;color:#a855f7!important; }
    .post-hist-row:hover { background:rgba(168,85,247,.05)!important; }

    .post-ticker-wrap { overflow:hidden;white-space:nowrap;padding:12px 0; }
    .post-ticker-inner { display:inline-block;animation:post-ticker 28s linear infinite; }
  `;
  document.head.appendChild(s);
};

// ── Slide config ──────────────────────────────────────────────────────────────
const SLIDES = [
  {
    id: 'intro',
    kicker: 'The Future of Post-Production',
    headline: 'After the cameras\nstop — the real\nwork begins.',
    body: 'Post-production is where films live or die. 60% of a film\'s final impact is decided after the last day of shooting. ProducerBuddy\'s AI Post-Production Suite puts the full power of a Hollywood finishing studio into every creator\'s hands.',
    accent: '#a855f7',
    bg: 'radial-gradient(ellipse at 40% 60%, rgba(168,85,247,.09) 0%, transparent 55%)',
    type: 'intro',
  },
  {
    id: 'history',
    kicker: 'The Old World of Post',
    headline: 'Post-production\nused to take\nmonths. And cost\na fortune.',
    body: 'What once required a Soho editing suite, a VFX farm, and six different agencies — ProducerBuddy delivers in one unified AI platform.',
    accent: '#ef4444',
    bg: 'radial-gradient(ellipse at 60% 30%, rgba(239,68,68,.08) 0%, transparent 50%)',
    type: 'history',
  },
  {
    id: 'editing',
    kicker: 'Stage 01 — Editing',
    headline: 'AI that edits\nlike a seasoned\nfilm editor.',
    body: 'Feed your raw footage. ProducerBuddy\'s AI analyzes every cut, identifies the best takes, suggests pacing, and assembles a first cut — while you focus on the creative vision.',
    accent: '#3b82f6',
    bg: 'radial-gradient(ellipse at 20% 50%, rgba(59,130,246,.09) 0%, transparent 55%)',
    type: 'stage',
    stageNum: '01',
    stageLabel: 'Editing',
    tools: [
      { name: 'Smart cut detection', desc: 'AI identifies optimal cut points from emotion analysis' },
      { name: 'Take ranking AI', desc: 'Scores every take by performance, lighting, and audio quality' },
      { name: 'Pacing intelligence', desc: 'Matches cut rhythm to genre — thriller vs drama vs comedy' },
      { name: 'Auto rough cut', desc: 'First assembly cut ready in hours, not weeks' },
      { name: 'Continuity checker', desc: 'Catches continuity errors across 1000s of frames' },
      { name: 'Color pre-grade', desc: 'AI-powered initial color pass based on director\'s reference' },
    ],
  },
  {
    id: 'vfx',
    kicker: 'Stage 02 — Visual Effects',
    headline: 'Hollywood-grade\nVFX. Pilomation\nAI. Your budget.',
    body: 'Green screen replacement, object removal, environment extension, sky replacement, and crowd simulation — all powered by the Pilomation VFX engine. No separate software. No specialist needed.',
    accent: '#06b6d4',
    bg: 'radial-gradient(ellipse at 75% 40%, rgba(6,182,212,.09) 0%, transparent 55%)',
    type: 'stage',
    stageNum: '02',
    stageLabel: 'Visual Effects',
    tools: [
      { name: 'AI background replacement', desc: 'Replace any background from a text prompt or reference image' },
      { name: 'Object & person removal', desc: 'Remove boom mics, wires, unwanted objects — frame perfect' },
      { name: 'Environment extension', desc: 'Extend sets digitally — make a 10x10 room look like a palace' },
      { name: 'Sky & lighting AI', desc: 'Replace sky, change time of day, match lighting across scenes' },
      { name: 'Crowd simulation', desc: 'Generate thousands of crowd members from a few extras' },
      { name: 'De-aging & enhancement', desc: 'Facial de-aging, blemish removal, eye enhancement' },
    ],
  },
  {
    id: 'marketing',
    kicker: 'Stage 03 — Marketing Analysis',
    headline: 'Know your audience\nbefore the film\never releases.',
    body: 'ProducerBuddy analyzes your cut for emotional beats, genre signals, demographic appeal, and market positioning — giving you a full marketing strategy before you spend a single rupee on promotion.',
    accent: '#f59e0b',
    bg: 'radial-gradient(ellipse at 30% 60%, rgba(245,158,11,.08) 0%, transparent 55%)',
    type: 'stage',
    stageNum: '03',
    stageLabel: 'Marketing Analysis',
    tools: [
      { name: 'Audience sentiment AI', desc: 'Predicts emotional response by demographic and region' },
      { name: 'Genre & tone analysis', desc: 'Maps your film to market segments and comp titles' },
      { name: 'Release window optimizer', desc: 'AI recommends optimal release date based on market data' },
      { name: 'Competitor landscape', desc: 'Real-time analysis of competing releases in your window' },
      { name: 'Social buzz predictor', desc: 'Forecasts viral potential of key scenes and moments' },
      { name: 'ROI scenario modeling', desc: 'Budget vs return projections across distribution channels' },
    ],
  },
  {
    id: 'ads',
    kicker: 'Stage 04 — Advertisements',
    headline: 'AI-generated ads\nthat actually\nconvert.',
    body: 'From 15-second Instagram reels to full 3-minute theatrical ads — ProducerBuddy generates, tests, and optimizes ad content from your footage automatically.',
    accent: '#ec4899',
    bg: 'radial-gradient(ellipse at 65% 35%, rgba(236,72,153,.08) 0%, transparent 55%)',
    type: 'stage',
    stageNum: '04',
    stageLabel: 'Advertisements',
    tools: [
      { name: 'Ad format generator', desc: '15s / 30s / 60s / 3min cuts generated from your film automatically' },
      { name: 'Platform optimizer', desc: 'Instagram, YouTube, OTT, cinema — each format optimized separately' },
      { name: 'Hook detection AI', desc: 'Identifies the most attention-grabbing 3 seconds from your footage' },
      { name: 'A/B variant creator', desc: 'Generate 5 ad variants and let AI predict best performer' },
      { name: 'Subtitle & caption AI', desc: 'Auto-captions for accessibility and silent-scroll audiences' },
      { name: 'Regional localization', desc: 'Adapt ads for different language markets with AI dubbing' },
    ],
  },
  {
    id: 'posters',
    kicker: 'Stage 05 — Posters',
    headline: 'A poster that stops\nthe scroll. Every\nsingle time.',
    body: 'Describe your film\'s tone and ProducerBuddy generates a dozen poster concepts — cinematic, typographic, minimalist, or dramatic — all at print resolution. No designer needed on day one.',
    accent: '#a855f7',
    bg: 'radial-gradient(ellipse at 45% 70%, rgba(168,85,247,.09) 0%, transparent 55%)',
    type: 'stage',
    stageNum: '05',
    stageLabel: 'Posters',
    tools: [
      { name: 'AI poster generation', desc: 'Text-to-poster from your film\'s brief, stills, and tone' },
      { name: 'Style library', desc: 'Bollywood, Hollywood, Art house, Noir, Thriller — 50+ styles' },
      { name: 'Typography AI', desc: 'Auto-selects typefaces that match your film\'s emotional register' },
      { name: 'Tagline generator', desc: 'AI writes 20 tagline options from your script synopsis' },
      { name: 'Print-ready export', desc: 'High-resolution files ready for theatre hoardings and print' },
      { name: 'Social-size adapts', desc: 'One click — poster resizes for every social platform' },
    ],
  },
  {
    id: 'trailers',
    kicker: 'Stage 06 — Trailers',
    headline: 'A trailer that\nmakes audiences\ncount the days.',
    body: 'The Pilomation AI watches your full film, identifies the most emotionally charged moments, builds a three-act trailer structure, and syncs it to music — delivering a teaser that creates genuine anticipation.',
    accent: '#10b981',
    bg: 'radial-gradient(ellipse at 20% 40%, rgba(16,185,129,.08) 0%, transparent 55%)',
    type: 'stage',
    stageNum: '06',
    stageLabel: 'Trailers',
    tools: [
      { name: 'Scene impact scoring', desc: 'AI ranks every scene by emotional punch and visual impact' },
      { name: '3-act trailer builder', desc: 'Hook → Rising tension → Climax structure generated automatically' },
      { name: 'Music sync AI', desc: 'Matches licensed music to your trailer\'s emotional arc perfectly' },
      { name: 'Teaser & full-length', desc: 'Generates 30s teaser + 2-minute trailer from same footage' },
      { name: 'Audience test simulation', desc: 'Predicts audience reaction before you release publicly' },
      { name: 'Multi-language versioning', desc: 'Dubbed trailers for Hindi, Tamil, Telugu, English markets' },
    ],
  },
  {
    id: 'distribution',
    kicker: 'Stage 07 — Distribution',
    headline: 'Your film. Every\nscreen. Every\nplatform.',
    body: 'ProducerBuddy\'s distribution network connects your film directly to OTT platforms, festival circuits, regional distributors, and theatrical chains — with AI-powered deal analysis so you never undersell your work.',
    accent: '#f59e0b',
    bg: 'radial-gradient(ellipse at 60% 50%, rgba(245,158,11,.09) 0%, transparent 55%)',
    type: 'distribution',
  },
  {
    id: 'cta',
    kicker: 'One platform. Complete post.',
    headline: 'Your film deserves\na finish worthy\nof its beginning.',
    body: 'Every stage of post-production. Every tool your team needs. Every audience your film deserves to reach. All powered by Pilomation AI. All inside ProducerBuddy.',
    accent: '#a855f7',
    bg: 'radial-gradient(ellipse at 50% 50%, rgba(168,85,247,.12) 0%, transparent 65%)',
    type: 'cta',
  },
];

const DURATION = 7000;
const TYPE_SPEED = 34;

const HISTORY_DATA = [
  { task: 'Rough cut editing', old: '3–6 weeks', oldcost: '₹3–8L', ai: '2–4 hours', aicost: 'Included' },
  { task: 'VFX & compositing', old: '2–6 months', oldcost: '₹15–50L', ai: '24–72 hours', aicost: 'Included' },
  { task: 'Theatrical poster', old: '1–2 weeks', oldcost: '₹1–3L', ai: '10 minutes', aicost: 'Included' },
  { task: 'Trailer production', old: '3–4 weeks', oldcost: '₹5–12L', ai: '4–8 hours', aicost: 'Included' },
  { task: 'Distribution deals', old: '3–9 months', oldcost: 'Agent 15–25%', ai: 'Direct platform', aicost: '0% cut' },
  { task: 'Marketing strategy', old: '6–8 weeks', oldcost: '₹8–20L agency', ai: 'AI-generated', aicost: 'Included' },
];

const DISTRIBUTION_CHANNELS = [
  { type: 'OTT Platforms', items: ['Netflix', 'Amazon Prime', 'Disney+ Hotstar', 'Zee5', 'SonyLiv', 'MX Player'], color: '#f59e0b', bg: 'rgba(245,158,11,.08)', border: 'rgba(245,158,11,.2)' },
  { type: 'Film Festivals', items: ['International circuit submission', 'AI-matched festival selection', 'Submission calendar automation', 'Press kit generation'], color: '#a855f7', bg: 'rgba(168,85,247,.08)', border: 'rgba(168,85,247,.2)' },
  { type: 'Theatrical', items: ['PVR · INOX · Cinepolis', 'Regional theatre networks', 'Single-screen outreach', 'Tax compliance automation'], color: '#10b981', bg: 'rgba(16,185,129,.08)', border: 'rgba(16,185,129,.2)' },
  { type: 'Digital Direct', items: ['YouTube monetization', 'Instagram reels strategy', 'WhatsApp broadcast', 'Telegram channel'], color: '#3b82f6', bg: 'rgba(59,130,246,.08)', border: 'rgba(59,130,246,.2)' },
];

const ALL_STAGES = [
  { num: '01', label: 'Editing', color: '#3b82f6', icon: '✂' },
  { num: '02', label: 'VFX', color: '#06b6d4', icon: '✦' },
  { num: '03', label: 'Marketing', color: '#f59e0b', icon: '◈' },
  { num: '04', label: 'Ads', color: '#ec4899', icon: '▶' },
  { num: '05', label: 'Posters', color: '#a855f7', icon: '◻' },
  { num: '06', label: 'Trailers', color: '#10b981', icon: '⬡' },
  { num: '07', label: 'Distribution', color: '#f59e0b', icon: '◎' },
];

function useTypewriter(text, active) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    clearInterval(ref.current); setDisplayed(''); setDone(false);
    if (!active) return;
    let i = 0; const chars = text.split('');
    ref.current = setInterval(() => {
      if (i >= chars.length) { clearInterval(ref.current); setDone(true); return; }
      setDisplayed(p => p + chars[i++]);
    }, TYPE_SPEED);
    return () => clearInterval(ref.current);
  }, [text, active]);
  return { displayed, done };
}

function Headline({ text, active, accent }) {
  const { displayed, done } = useTypewriter(text, active);
  return (
    <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(30px,5vw,56px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.02em', color: '#f8fafc', minHeight: '4em', marginBottom: 24 }}>
      {displayed.split('\n').map((l, i) => <span key={i} style={{ display: 'block' }}>{l}</span>)}
      {!done && <span className="post-cursor" style={{ background: accent }} />}
    </div>
  );
}

function Particles({ accent }) {
  const pts = useRef(Array.from({ length: 18 }, (_, i) => ({
    id: i, size: Math.random() * 5 + 2,
    left: Math.random() * 100, top: Math.random() * 100,
    dur: Math.random() * 11 + 8, delay: Math.random() * 8,
    isAccent: Math.random() > 0.5, shape: Math.random() > 0.7 ? 'diamond' : 'circle',
  }))).current;
  return <>
    {pts.map(p => (
      <div key={p.id} className="post-particle" style={{
        width: p.size, height: p.size,
        background: p.isAccent ? accent : 'rgba(255,255,255,.2)',
        left: `${p.left}%`, top: `${p.top}%`,
        borderRadius: p.shape === 'circle' ? '50%' : '2px',
        transform: p.shape === 'diamond' ? 'rotate(45deg)' : undefined,
        animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s`, opacity: 0,
      }} />
    ))}
  </>;
}

function TickerBar({ accent }) {
  const items = ['AI Editing', '·', 'Visual Effects', '·', 'Marketing AI', '·', 'Ad Generation', '·', 'Poster AI', '·', 'Trailer Builder', '·', 'Distribution Network', '·'];
  const doubled = [...items, ...items];
  return (
    <div className="post-ticker-wrap" style={{ borderTop: `1px solid ${accent}20`, borderBottom: `1px solid ${accent}20`, margin: '20px 0' }}>
      <div className="post-ticker-inner">
        {doubled.map((item, i) => (
          <span key={i} style={{ marginRight: 32, fontSize: 12, letterSpacing: '1.5px', textTransform: 'uppercase', color: item === '·' ? '#334155' : accent, fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}>{item}</span>
        ))}
      </div>
    </div>
  );
}

function IntroContent({ visible }) {
  return (
    <div style={{ opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.3s' }}>
      <TickerBar accent="#a855f7" />
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        {ALL_STAGES.map((s, i) => (
          <div key={i} style={{
            padding: '12px 18px', borderRadius: 10, textAlign: 'center', minWidth: 90,
            background: `${s.color}10`, border: `1px solid ${s.color}30`,
          }}>
            <div style={{ fontSize: 18, color: s.color, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 10, letterSpacing: '1.5px', textTransform: 'uppercase', color: s.color, fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryContent({ visible }) {
  return (
    <div style={{ maxWidth: 820, margin: '24px auto 0', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.2s', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Manrope', sans-serif" }}>
        <thead>
          <tr>
            {['Post-production task', 'Old time', 'Old cost', 'ProducerBuddy AI', 'Cost'].map(h => (
              <th key={h} style={{ padding: '10px 14px', fontSize: 10, letterSpacing: '2px', textTransform: 'uppercase', color: '#475569', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,.08)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {HISTORY_DATA.map((row, i) => (
            <tr key={i} className="post-hist-row" style={{ transition: 'background .2s' }}>
              <td style={{ padding: '12px 14px', fontSize: 13, color: '#cbd5e1', borderBottom: '1px solid rgba(255,255,255,.05)', fontWeight: 500 }}>{row.task}</td>
              <td style={{ padding: '12px 14px', fontSize: 12, color: '#ef4444', borderBottom: '1px solid rgba(255,255,255,.05)', textDecoration: 'line-through' }}>{row.old}</td>
              <td style={{ padding: '12px 14px', fontSize: 12, color: '#ef4444', borderBottom: '1px solid rgba(255,255,255,.05)', textDecoration: 'line-through' }}>{row.oldcost}</td>
              <td style={{ padding: '12px 14px', fontSize: 13, color: '#a855f7', borderBottom: '1px solid rgba(255,255,255,.05)', fontWeight: 600 }}>{row.ai}</td>
              <td style={{ padding: '12px 14px', fontSize: 13, color: '#10b981', borderBottom: '1px solid rgba(255,255,255,.05)', fontWeight: 700 }}>{row.aicost}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StageContent({ slide, visible }) {
  return (
    <div style={{ opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.3s' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', color: '#475569', fontFamily: "'Manrope', sans-serif" }}>Stage</div>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 40, fontWeight: 800, color: slide.accent, opacity: .2 }}>{slide.stageNum}</div>
        <div style={{ height: 32, width: 1, background: 'rgba(255,255,255,.1)' }} />
        <div style={{ display: 'flex', gap: 8 }}>
          {ALL_STAGES.map((s, i) => (
            <div key={i} style={{
              width: 6, height: 6, borderRadius: '50%',
              background: s.label === slide.stageLabel ? slide.accent : 'rgba(255,255,255,.15)',
              transform: s.label === slide.stageLabel ? 'scale(1.6)' : 'scale(1)',
              transition: 'all .3s',
            }} />
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, maxWidth: 720, margin: '0 auto' }}>
        {slide.tools.map((tool, i) => (
          <div key={i} className="post-tool-item" style={{
            padding: '16px 14px', background: `${slide.accent}08`,
            border: `1px solid ${slide.accent}18`, borderRadius: 12,
            transition: 'background .2s, border-color .2s',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 6, fontFamily: "'Manrope', sans-serif" }}>{tool.name}</div>
            <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.55, fontFamily: "'Manrope', sans-serif" }}>{tool.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DistributionContent({ visible }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14, maxWidth: 820, margin: '24px auto 0', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.3s' }}>
      {DISTRIBUTION_CHANNELS.map((ch, i) => (
        <div key={i} style={{ padding: '20px 18px', background: ch.bg, border: `1px solid ${ch.border}`, borderRadius: 14 }}>
          <div style={{ fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', color: ch.color, marginBottom: 14, fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}>{ch.type}</div>
          {ch.items.map((item, j) => (
            <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: ch.color, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#94a3b8', fontFamily: "'Manrope', sans-serif" }}>{item}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function CTAContent({ visible, navigate }) {
  return (
    <div style={{ opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.5s' }}>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', margin: '28px 0 32px' }}>
        {ALL_STAGES.map((s, i) => (
          <div key={i} style={{
            padding: '10px 16px', borderRadius: 8,
            background: `${s.color}10`, border: `1px solid ${s.color}25`,
            fontSize: 12, color: s.color, fontFamily: "'Manrope', sans-serif", fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span>{s.icon}</span>{s.label}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 13, color: '#475569', marginBottom: 24, fontFamily: "'Manrope', sans-serif" }}>
        All 7 stages. One monthly plan. Zero separate agencies.
      </div>
      <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button className="post-btn-p" onClick={() => navigate('/signup')} style={{
          padding: '14px 32px', background: '#a855f7', color: '#fff',
          border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
          cursor: 'pointer', transition: 'transform .2s, box-shadow .2s',
          fontFamily: "'Manrope', sans-serif",
        }}>Start post-production →</button>
        <button className="post-btn-g" onClick={() => navigate('/post_dash')} style={{
          padding: '14px 32px', background: 'transparent', color: '#94a3b8',
          border: '1px solid rgba(255,255,255,.15)', borderRadius: 10, fontSize: 14,
          cursor: 'pointer', transition: 'all .2s', fontFamily: "'Manrope', sans-serif",
        }}>Explore the suite</button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
const PostProductionGuide = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [progKey, setProgKey] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => { injectStyles(); setVisible(true); }, []);

  const goTo = useCallback((n) => {
    const next = ((n % SLIDES.length) + SLIDES.length) % SLIDES.length;
    setVisible(false);
    setTimeout(() => { setCurrent(next); setProgKey(k => k + 1); setVisible(true); }, 280);
  }, []);

  useEffect(() => {
    if (paused) { clearTimeout(timerRef.current); return; }
    timerRef.current = setTimeout(() => goTo(current + 1), DURATION);
    return () => clearTimeout(timerRef.current);
  }, [current, paused, goTo]);

  const slide = SLIDES[current];

  return (
    <div className="pb-post" style={{ minHeight: '100vh', background: '#05040d', color: '#e8edf2', display: 'flex', flexDirection: 'column' }}>
      <SEO
        title="Post-Production Guide"
        description="Everything you need to know about post-production: editing, VFX, colour grading, sound design, and digital delivery — guided by ProducerBuddy."
        url="https://producerbuddy.pilomation.com/post-production-guide"
      />
      <Nav />

      {!paused && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 200, background: 'rgba(255,255,255,.05)' }}>
          <div key={progKey} className="post-prog-bar" style={{
            height: '100%',
            background: `linear-gradient(90deg, ${slide.accent}, ${slide.accent}99)`,
            animationDuration: `${DURATION}ms`, animationTimingFunction: 'linear', animationFillMode: 'both',
          }} />
        </div>
      )}

      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: slide.bg, transition: 'background .8s', zIndex: 0 }} />
        <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}><Particles accent={slide.accent} /></div>

        <div className={visible ? 'post-slide-enter' : ''} style={{
          position: 'relative', zIndex: 2, maxWidth: 940, width: '100%',
          padding: '44px 36px', textAlign: 'center', opacity: visible ? 1 : 0,
        }}>
          <div style={{
            fontSize: 10, letterSpacing: '3px', textTransform: 'uppercase',
            color: slide.accent, marginBottom: 18, fontFamily: "'Manrope', sans-serif", fontWeight: 600,
            opacity: visible ? 1 : 0, transition: 'opacity .5s ease .2s',
          }}>{slide.kicker}</div>

          <Headline text={slide.headline} active={visible} accent={slide.accent} />

          {slide.body && (
            <p style={{
              fontSize: 15, lineHeight: 1.8, color: '#94a3b8', maxWidth: 580,
              margin: '0 auto 8px', fontFamily: "'Manrope', sans-serif",
              opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.2s',
            }}>{slide.body}</p>
          )}

          {slide.type === 'intro' && <IntroContent visible={visible} />}
          {slide.type === 'history' && <HistoryContent visible={visible} />}
          {slide.type === 'stage' && <StageContent slide={slide} visible={visible} />}
          {slide.type === 'distribution' && <DistributionContent visible={visible} />}
          {slide.type === 'cta' && <CTAContent visible={visible} navigate={navigate} />}
        </div>
      </div>

      <div style={{ padding: '16px 0 14px', background: 'rgba(5,4,13,.93)', backdropFilter: 'blur(12px)', zIndex: 10 }}>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 12 }}>
          {SLIDES.map((sl, i) => (
            <div key={i} className={i === current ? 'post-dot-active' : ''} onClick={() => goTo(i)} style={{
              width: 6, height: 6, borderRadius: '50%',
              background: 'rgba(255,255,255,.18)', cursor: 'pointer', transition: 'all .3s',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          {['← prev', paused ? '▶ play' : '⏸ pause', 'next →'].map((label, i) => (
            <button key={i} className="post-ctrl" onClick={() => {
              if (i === 0) goTo(current - 1);
              else if (i === 1) setPaused(p => !p);
              else goTo(current + 1);
            }} style={{
              background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)',
              color: '#94a3b8', padding: '8px 20px', borderRadius: 6, fontSize: 12,
              cursor: 'pointer', transition: 'all .2s', fontFamily: "'Manrope', sans-serif",
            }}>{label}</button>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11, color: '#1e1b2e', fontFamily: "'Manrope', sans-serif", letterSpacing: '1px' }}>
          {current + 1} / {SLIDES.length} — {slide.kicker}
        </div>
      </div>
    </div>
  );
};

export default PostProductionGuide;
